import { Button, RadioGroup, Tag } from '@ads/components-react';
import { CalendarDaysIcon, PaperAirplaneIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';

import { useNotificationReview } from '../../../hooks/useNotificationReview';
import type { NotificationSendMode } from '../../../types/notification';
import { ScheduleDateTimePicker } from '../../molecules/ScheduleDateTimePicker';
import { SendModeCard } from '../../molecules/SendModeCard';

/**
 * Etapa 3 do wizard de notificação — revisão e envio.
 *
 * Exibe o card de resumo (público, mensagem, URL) e o card de forma de envio
 * (imediato ou agendado com seleção de data e hora inline).
 */
export function NotificationReviewStep() {
  const { t } = useTranslation('notification');
  const {
    title,
    body,
    url,
    studentCount,
    classroomNameList,
    statusLabels,
    schedule,
    updateSchedule,
    goToMessageStep,
  } = useNotificationReview();

  const handleModeChange = (mode: NotificationSendMode) => {
    if (mode === 'now') {
      updateSchedule({ mode: 'now', scheduledAt: null });
    } else {
      updateSchedule({ mode: 'scheduled', scheduledAt: null });
    }
  };

  return (
    <div className="flex flex-col gap-mini p-xxs">
      <div>
        <h2 className="ts-heading-sm text-ctx-content-title">{t('wizard.review.title')}</h2>
        <p className="ts-paragraph-xs text-ctx-content-base">{t('wizard.review.subtitle')}</p>
      </div>

      {/* Card Resumo */}
      <div className="rounded-md py-mini px-xxs bg-ctx-layout-body p-mini">
        <div className="flex items-center justify-between mb-xs">
          <span className="ts-subtitle-xs text-ctx-content-title">
            {t('wizard.review.summary.title')}
          </span>
          <Button
            hierarchy="secondary"
            size="sm"
            leadingIcon={PencilSquareIcon as ComponentType<{ className?: string }>}
            onClick={goToMessageStep}
          >
            {t('wizard.review.summary.editContent')}
          </Button>
        </div>
        <div className="flex flex-col gap-xxs">
          <div className="grid grid-cols-1 gap-xxs ">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-micro">
              <div className="flex flex-col gap-quark">
                <span className="ts-subtitle-xs text-ctx-content-title">
                  {t('wizard.review.summary.audienceLabel')}
                </span>
                <span className="ts-paragraph-xs text-ctx-content-base">
                  {t('wizard.review.summary.studentsCount', { count: studentCount })}
                </span>
              </div>
              <div className="flex flex-col gap-quark">
                <span className="ts-subtitle-xs text-ctx-content-title">
                  {t('wizard.review.summary.notificationTitleLabel')}
                </span>
                <span className="ts-paragraph-xs text-ctx-content-base">{title}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-micro">
              <div className="flex flex-col gap-quark">
                <span className="ts-subtitle-xs text-ctx-content-title">
                  {t('wizard.review.summary.classroomsLabel')}
                </span>
                <div className="flex flex-col gap-quark">
                  {classroomNameList.map(name => (
                    <span key={name} className="ts-paragraph-xs text-ctx-content-base">
                      {name}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-quark">
                <span className="ts-subtitle-xs text-ctx-content-title">
                  {t('wizard.review.summary.notificationBodyLabel')}
                </span>
                <span className="ts-paragraph-xs text-ctx-content-base whitespace-pre-wrap">
                  {body}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-xxs">
            <div className="flex flex-col gap-quark">
              <span className="ts-subtitle-xs text-ctx-content-title">
                {t('wizard.review.summary.enrollmentStatusLabel')}
              </span>
              <div className="flex flex-wrap gap-nano">
                {statusLabels.map(label => (
                  <Tag key={label} label={label} size="sm" custom={{ text: 'text-nowrap' }} />
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-quark">
              <span className="ts-subtitle-xs text-ctx-content-title">
                {t('wizard.review.summary.urlLabel')}
              </span>
              <span className="ts-paragraph-xs text-ctx-content-base">
                {url || t('wizard.review.summary.noUrl')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Card Forma de envio */}
      <div className="rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface py-mini px-xxs ">
        <span className="ts-heading-xs text-ctx-content-title mb-mini block">
          {t('wizard.review.sendMode.title')}
        </span>

        <RadioGroup
          value={schedule.mode}
          onValueChange={v => handleModeChange(v as NotificationSendMode)}
          className="grid grid-cols-2 gap-mini"
        >
          <SendModeCard
            value="scheduled"
            selected={schedule.mode === 'scheduled'}
            icon={CalendarDaysIcon as ComponentType<{ className?: string }>}
            title={t('wizard.review.sendMode.scheduled')}
            description={t('wizard.review.sendMode.scheduledDescription')}
            onSelect={handleModeChange}
          />
          <SendModeCard
            value="now"
            selected={schedule.mode === 'now'}
            icon={PaperAirplaneIcon as ComponentType<{ className?: string }>}
            title={t('wizard.review.sendMode.immediate')}
            description={t('wizard.review.sendMode.immediateDescription')}
            onSelect={handleModeChange}
          />
        </RadioGroup>

        {schedule.mode === 'scheduled' && (
          <ScheduleDateTimePicker
            value={schedule.scheduledAt}
            onChange={scheduledAt => updateSchedule({ scheduledAt })}
          />
        )}
      </div>
    </div>
  );
}
